import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  useMediaQuery,
  Fade,
  Slide,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";

// MUI Icons
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import ExploreIcon from "@mui/icons-material/Explore";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

type ModuleKey = "AI_CORE" | "COREMIND" | "PATHFINDER" | "HEARTCORE";

/* ====================== HOOK: reveal theo scroll ====================== */
function useInViewOnce(threshold = 0.18) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, inView };
}

/* ====================== WRAPPER: animation cho section ====================== */
function SectionReveal({
  children,
  variant = "fade",
  timeout = 650,
  threshold = 0.18,
}: {
  children: React.ReactNode;
  variant?: "fade" | "slideUp";
  timeout?: number;
  threshold?: number;
}) {
  const { ref, inView } = useInViewOnce(threshold);

  if (variant === "slideUp") {
    return (
      <div ref={ref}>
        <Slide direction="up" in={inView} timeout={timeout} mountOnEnter>
          <div>{children}</div>
        </Slide>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <Fade in={inView} timeout={timeout}>
        <div>{children}</div>
      </Fade>
    </div>
  );
}

/* ============================= PAGE ============================= */

export default function AboutLumoraPage() {
  const flowRef = useRef<HTMLDivElement | null>(null);

  const scrollToFlow = () => {
    flowRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <Box
      sx={{
        bgcolor: "#f7f7ff",
      }}
    >
      <Fade in timeout={600}>
        <div>
          <HeroSection onScrollToFlow={scrollToFlow} />
        </div>
      </Fade>

      <BenefitsSection />

      <div ref={flowRef}>
        <FlowSection />
      </div>

      <ModulesSection />

      <QuoteCTASection />

      <FooterMini />
    </Box>
  );
}

/* ----------------------------- 1) HERO ----------------------------- */

function HeroSection({ onScrollToFlow }: { onScrollToFlow: () => void }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const history = useHistory();

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 7, md: 10 },
        background:
          "radial-gradient(1200px 500px at 15% 20%, rgba(99,102,241,0.25), transparent 60%)," +
          "radial-gradient(900px 500px at 85% 30%, rgba(59,130,246,0.22), transparent 55%)," +
          "linear-gradient(135deg, #0b1026 0%, #141a3a 45%, #0b1026 100%)",
        color: "white",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.25,
          background:
            "radial-gradient(circle at 20% 80%, rgba(236,72,153,0.35), transparent 45%)," +
            "radial-gradient(circle at 80% 70%, rgba(34,197,94,0.28), transparent 40%)",
          filter: "blur(10px)",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  icon={<StarRoundedIcon />}
                  label="La bàn định vị tiềm năng & tương lai cá nhân"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "white",
                    border: "1px solid rgba(255,255,255,0.18)",
                    "& .MuiChip-icon": { color: "white" },
                  }}
                />
              </Stack>

              <Typography
                variant={isMdUp ? "h2" : "h3"}
                sx={{ fontWeight: 800, letterSpacing: -0.5, lineHeight: 1.05 }}
              >
                EduCompass
              </Typography>

              <Typography
                variant="h6"
                sx={{ color: "rgba(255,255,255,0.82)", maxWidth: 620 }}
              >
                Hiểu năng lực học tập • Chăm sóc cảm xúc • Định hướng tương lai —
                tất cả được kết nối trong một nền tảng dành riêng cho học sinh THPT
              </Typography>

              <Typography
                sx={{ color: "rgba(255,255,255,0.72)", maxWidth: 640 }}
              >
                EduCompass giúp bạn nhìn rõ hơn về chính mình thông qua dữ liệu học tập
                và những tương tác hằng ngày — từ đó học chủ động hơn, nhẹ nhàng hơn
                và tự tin hơn theo cách của riêng bạn.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RocketLaunchIcon />}
                  onClick={() => history.push("/dashboard")}
                  sx={{
                    bgcolor: "#6366f1",
                    "&:hover": { bgcolor: "#5458e6" },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Khám phá EduCompass
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={onScrollToFlow}
                  sx={{
                    borderColor: "rgba(255,255,255,0.35)",
                    color: "white",
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": {
                      borderColor: "rgba(255,255,255,0.55)",
                      bgcolor: "rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  Xem cách EduCompass hoạt động
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <MockupCard />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function MockupCard() {
  return (
    <Card
      sx={{
        borderRadius: 4,
        bgcolor: "rgba(255, 255, 255, 0.83)",
        border: "1px solid rgba(255, 255, 255, 0.65)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 18px 60px rgba(129, 121, 121, 0.65)",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
          Snapshot cá nhân hóa
        </Typography>

        <Stack spacing={1.25}>
          <MiniStat label="Học lực" value="Phân tích điểm mạnh/yếu theo môn" />
          <MiniStat
            label="Cảm xúc"
            value="Ghi nhận trạng thái & hỗ trợ tức thời"
          />
          <MiniStat
            label="Định hướng"
            value="Gợi ý nghề nghiệp + lộ trình rõ ràng"
          />
        </Stack>

        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.16)" }} />

        <Box
          sx={{
            height: 160,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(79,70,229,0.35)",
            border: "1px solid rgba(79,70,229,0.25)",
          }}
        >
          <Box
            component="img"
            src="/media/avatars/educompass.png"
            alt="EduCompass cover"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: "1px solid rgba(255,255,255,0.12)",
        bgcolor: "rgba(255,255,255,0.04)",
      }}
    >
      <Typography variant="caption" sx={{ opacity: 0.75 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700 }}>{value}</Typography>
    </Box>
  );
}

/* -------------------------- 2) BENEFITS --------------------------- */

function BenefitsSection() {
  return (
    <SectionReveal variant="fade" timeout={700}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: -0.3 }}>
            EduCompass giúp bạn làm được gì?
          </Typography>
          <Typography sx={{ color: "text.secondary", maxWidth: 760 }}>
            Mọi gợi ý đều hướng tới sự rõ ràng, cá nhân hóa và dễ thực hiện —
            để bạn học đúng hướng, thay vì chỉ cố gắng học thật nhiều.
          </Typography>
        </Stack>

        <Grid container spacing={2.5} sx={{ mt: 2 }}>
          {[
            {
              icon: <AutoGraphIcon />,
              title: "Hiểu năng lực học tập",
              desc: "Giúp bạn nhận ra điểm mạnh, điểm cần cải thiện và theo dõi tiến trình học dựa trên dữ liệu thật.",
            },
            {
              icon: <ExploreIcon />,
              title: "Định hướng nghề nghiệp",
              desc: "Gợi ý những hướng nghề nghiệp phù hợp, kèm theo lý do và lộ trình để bạn dễ hình dung và lựa chọn.",
            },
            {
              icon: <FavoriteIcon />,
              title: "Chăm sóc cảm xúc",
              desc: "Nhận diện cảm xúc, giúp bạn giải tỏa áp lực và giữ cân bằng tinh thần trong quá trình học tập.",
            },
            {
              icon: <SmartToyIcon />,
              title: "Trợ lý học tập AI",
              desc: "Hỗ trợ giải thích bài, gợi ý cách luyện tập và đồng hành cùng bạn trong việc học mỗi ngày.",
            },
          ].map((it) => (
            <Grid key={it.title} item xs={12} sm={6} md={3}>
              <BenefitCard icon={it.icon} title={it.title} desc={it.desc} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </SectionReveal>
  );
}

function BenefitCard({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 4,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        border: "1px solid rgba(99,102,241,0.10)",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(99,102,241,0.12)",
            color: "#4f46e5",
            mb: 1.5,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 900, mb: 0.75 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {desc}
        </Typography>
      </CardContent>
    </Card>
  );
}

/* ----------------------------- 3) FLOW ----------------------------- */

function FlowSection() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const steps = useMemo(
    () => [
      {
        title: "Thu thập dữ liệu",
        desc: "Thu thập những thông tin cần thiết như điểm số, môn học, kỹ năng, mục tiêu học tập và (nếu bạn muốn) trạng thái cảm xúc.",
        bullets: ["Hồ sơ học tập", "Hồ sơ năng lực", "Tương tác cảm xúc"],
      },
      {
        title: "AI phân tích & cá nhân hóa",
        desc: "Phân tích dữ liệu để nhận ra điểm mạnh, điểm cần cải thiện, xu hướng tiến bộ và những hỗ trợ bạn đang cần.",
        bullets: ["Phân tích theo thời gian", "Xác định ưu tiên", "Gợi ý rõ ràng"],
      },
      {
        title: "Gợi ý học tập & nghề nghiệp",
        desc: "Gợi ý lộ trình học tập, kỹ năng cần bổ sung và những hướng nghề nghiệp phù hợp với năng lực hiện tại.",
        bullets: ["Lộ trình cải thiện", "Kỹ năng/chứng chỉ", "Lý do đề xuất"],
      },
      {
        title: "Đồng hành & tối ưu liên tục",
        desc: "Đồng hành lâu dài bằng cách theo dõi tiến bộ, cập nhật mục tiêu và điều chỉnh gợi ý khi bạn thay đổi.",
        bullets: ["Báo cáo tiến trình", "Điều chỉnh mục tiêu", "Phát triển bền vững"],
      },
    ],
    []
  );

  return (
    <SectionReveal variant="slideUp" timeout={750}>
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: "white" }}>
        <Container maxWidth="lg">
          <Stack spacing={1}>
            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: -0.3 }}>
              Hành trình EduCompass đồng hành
            </Typography>
          </Stack>

          <Grid container spacing={2.5} sx={{ mt: 2 }}>
            {steps.map((s, idx) => (
              <Grid key={s.title} item xs={12} md={6}>
                <Card
                  sx={{
                    borderRadius: 4,
                    border: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          minWidth: 34,
                          height: 34,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "rgba(59,130,246,0.12)",
                          color: "#2563eb",
                          fontWeight: 900,
                        }}
                      >
                        {idx + 1}
                      </Box>

                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>{s.title}</Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", mt: 0.5 }}
                        >
                          {s.desc}
                        </Typography>

                        <Stack
                          direction={isMdUp ? "row" : "column"}
                          spacing={1}
                          sx={{ mt: 1.5, flexWrap: "wrap" }}
                        >
                          {s.bullets.map((b) => (
                            <Chip
                              key={b}
                              label={b}
                              size="small"
                              sx={{
                                bgcolor: "rgba(2,132,199,0.10)",
                                color: "#075985",
                                fontWeight: 700,
                                borderRadius: 2,
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </SectionReveal>
  );
}

/* ------------------------- 4) MODULES ------------------------- */

function ModulesSection() {
  const [active, setActive] = useState<ModuleKey>("AI_CORE");

  const modules = useMemo(
    () => ({
      AI_CORE: {
        title: "AI CORE — Trung tâm xử lý & điều phối thông minh",
        summary:
          "AI CORE là bộ não trung tâm của EduCompass, tiếp nhận dữ liệu từ các mô-đun, phân tích bằng thuật toán và phối hợp AI để tạo ra gợi ý học tập, nghề nghiệp và cảm xúc phù hợp với từng học sinh.",
        role: "Kết nối dữ liệu học tập, định hướng nghề nghiệp và cảm xúc học đường thành một hệ thống cá nhân hóa.",
        highlights: [
          "Tiếp nhận và điều phối dữ liệu từ COREMIND, PATHFINDER và HEARTCORE",
          "Kết hợp thuật toán phân tích dữ liệu với Gemini API",
          "Cá nhân hóa nội dung phản hồi theo từng người dùng",
          "Hỗ trợ diễn giải kết quả bằng ngôn ngữ dễ hiểu",
        ],
      },

      COREMIND: {
        title: "COREMIND — Trung tâm hỗ trợ học tập",
        summary:
          "COREMIND giúp học sinh nhập điểm, lưu trữ dữ liệu học tập, theo dõi tiến trình và phân tích điểm mạnh – điểm yếu theo từng môn học.",
        role: "Chuyển dữ liệu điểm số rời rạc thành thông tin học tập có ý nghĩa, giúp học sinh học có chiến lược hơn.",
        highlights: [
          "Nhập điểm thường xuyên, giữa kỳ, cuối kỳ",
          "Tính điểm trung bình có trọng số theo từng môn",
          "Theo dõi hành trình học tập và lịch sử tiến bộ",
          "Phân tích điểm mạnh – điểm yếu theo môn học",
          "Gợi ý môn cần ưu tiên và chiến lược cải thiện",
        ],
      },

      PATHFINDER: {
        title: "PATHFINDER — Trung tâm định hướng nghề nghiệp",
        summary:
          "PATHFINDER sử dụng dữ liệu học tập từ COREMIND và Career Requirement Matrix để đánh giá mức độ phù hợp giữa học sinh với các nhóm ngành nghề.",
        role: "Giúp học sinh định hướng nghề nghiệp dựa trên dữ liệu thay vì cảm tính.",
        highlights: [
          "Academic Match Score — đánh giá mức độ phù hợp học lực",
          "Skill Proxy Score — ước lượng kỹ năng từ dữ liệu học tập",
          "Career Readiness Score — chỉ số sẵn sàng nghề nghiệp",
          "Career Twin Simulation — mô phỏng phiên bản tương lai khi cải thiện điểm số",
          "PathFinder Forecast — dự báo hướng nghề nghiệp tiềm năng",
          "AI Explanation — giải thích lý do đề xuất bằng ngôn ngữ tự nhiên",
        ],
      },

      HEARTCORE: {
        title: "HEARTCORE — Trung tâm hỗ trợ cảm xúc học đường",
        summary:
          "HEARTCORE hỗ trợ học sinh nhận diện, chia sẻ và điều chỉnh trạng thái cảm xúc trong quá trình học tập, giúp việc học không chỉ xoay quanh điểm số mà còn quan tâm đến tinh thần.",
        role: "Giúp học sinh được lắng nghe, giảm áp lực và duy trì trạng thái học tập tích cực.",
        highlights: [
          "Void Zone — nơi chia sẻ áp lực, lo lắng hoặc cảm xúc tiêu cực",
          "AI phản hồi đồng cảm, hỗ trợ và động viên",
          "Cloud Whisper — thông điệp tích cực giúp cân bằng cảm xúc",
          "Khung hỗ trợ cảm xúc trực tiếp trên giao diện chính",
          "Các chế độ Lắng nghe, Giảm áp lực và Đồng hành",
        ],
      },
    }),
    []
  );

  const chipOrder: ModuleKey[] = [
    "AI_CORE",
    "COREMIND",
    "PATHFINDER",
    "HEARTCORE",
  ];

  const activeData = modules[active];

  return (
    <SectionReveal variant="fade" timeout={700}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: -0.3 }}>
            Hệ mô-đun chức năng của EduCompass
          </Typography>

          <Typography sx={{ color: "text.secondary", maxWidth: 820 }}>
            EduCompass được xây dựng theo mô hình một trung tâm AI CORE kết nối với
            ba mô-đun chính: COREMIND cho học tập, PATHFINDER cho định hướng
            nghề nghiệp và HEARTCORE cho hỗ trợ cảm xúc học đường.
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
        >
          {chipOrder.map((k) => (
            <Chip
              key={k}
              label={k.replace("_", " ")}
              onClick={() => setActive(k)}
              variant={active === k ? "filled" : "outlined"}
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                ...(active === k
                  ? { bgcolor: "#4f46e5", color: "white" }
                  : {
                      borderColor: "rgba(79,70,229,0.35)",
                      color: "#3730a3",
                    }),
              }}
            />
          ))}
        </Stack>

        <Grid container spacing={2.5} sx={{ mt: 2 }}>
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                borderRadius: 4,
                height: "100%",
                boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
                border: "1px solid rgba(79,70,229,0.10)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  {activeData.title}
                </Typography>

                <Typography sx={{ color: "text.secondary", mb: 2 }}>
                  {activeData.summary}
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "rgba(79,70,229,0.07)",
                    border: "1px solid rgba(79,70,229,0.12)",
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                    Vai trò chính
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {activeData.role}
                  </Typography>
                </Box>

                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  Tính năng nổi bật
                </Typography>

                <Stack spacing={1}>
                  {activeData.highlights.map((h) => (
                    <Stack
                      key={h}
                      direction="row"
                      spacing={1.25}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          mt: "7px",
                          borderRadius: 999,
                          bgcolor: "rgba(79,70,229,0.9)",
                          flexShrink: 0,
                        }}
                      />
                      <Typography>{h}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              sx={{
                borderRadius: 4,
                height: "100%",
                background:
                  "linear-gradient(135deg, rgba(79,70,229,0.10), rgba(59,130,246,0.08))",
                border: "1px solid rgba(79,70,229,0.12)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.06)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  Kiến trúc AI-Hybrid
                </Typography>

                <Typography sx={{ color: "text.secondary", mb: 2 }}>
                  EduCompass không giao toàn bộ việc đánh giá cho AI tạo sinh.
                  Hệ thống dùng thuật toán riêng để tính toán dữ liệu, sau đó dùng
                  Gemini API để diễn giải, cá nhân hóa phản hồi và hỗ trợ cảm xúc.
                </Typography>

                <Stack spacing={1.25}>
                  {[
                    "Data Layer: lưu trữ người dùng, điểm số, hồ sơ học tập và dữ liệu nghề nghiệp",
                    "Intelligence Layer: xử lý chỉ số, mô phỏng và dự báo định hướng",
                    "AI Layer: diễn giải kết quả, cá nhân hóa phản hồi bằng Gemini API",
                    "Presentation Layer: giao diện ReactJS, TypeScript, TailwindCSS thân thiện với học sinh",
                  ].map((item) => (
                    <Box
                      key={item}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.65)",
                        border: "1px solid rgba(255,255,255,0.8)",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </SectionReveal>
  );
}

/* ------------------------- 5) QUOTE + CTA ------------------------- */

function QuoteCTASection() {
  const history = useHistory();

  return (
    <SectionReveal variant="fade" timeout={700}>
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: "white",
          borderTop: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Container maxWidth="lg">
          <Card
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid rgba(79,70,229,0.14)",
              boxShadow: "0 14px 50px rgba(0,0,0,0.10)",
            }}
          >
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                background:
                  "linear-gradient(135deg, rgba(79,70,229,0.10), rgba(59,130,246,0.10))",
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 900, lineHeight: 1.25, mb: 1.25 }}
              >
                EduCompass là la bàn dẫn lối để bạn khám phá tiềm năng cá nhân,
                định hình tương lai và tự tin xây dựng con đường dành riêng cho mình.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mt: 2.5 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => history.push("/dashboard")}
                  sx={{
                    bgcolor: "#4f46e5",
                    "&:hover": { bgcolor: "#4338ca" },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 900,
                  }}
                >
                  Trải nghiệm EduCompass
                </Button>
              </Stack>
            </Box>
          </Card>
        </Container>
      </Box>
    </SectionReveal>
  );
}

/* ----------------------------- FOOTER ----------------------------- */

function FooterMini() {
  return (
    <SectionReveal variant="fade" timeout={500} threshold={0.12}>
      <Box sx={{ py: 3, bgcolor: "#f7f7ff" }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            © {new Date().getFullYear()} EduCompass • La bàn giáo dục sử dụng trí tuệ nhân tạo
          </Typography>
        </Container>
      </Box>
    </SectionReveal>
  );
}